import React from 'react'
import styled, { css } from 'styled-components'
import Link from 'next/link'
import StaticImage from '@components/images/StaticImg'
import BuyButton from '../../MwPage/components/BuyButton'
import PriceBlock from '../../MwPage/components/PriceBlock'
import RichText from '../../MwPage/components/RichText'
import { header, IDS } from '../../MwPage/content'
import { iceLine, ink, muted, PHONE, rise, ui } from '../../MwPage/styled'
import TrialCta from '../components/TrialCta'
import { useFocus } from '../focus'
import { Bullets, Column, GradientPill, Kicker, Wrap } from '../styled'

// the hero's pieces rise into place on load, a few hundredths apart, as on the MW page
const riseIn = (delay) => css`
  animation: ${rise} 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}s both;
`

const TrustStrip = styled.p`
  margin: 0;
  padding: 10px 16px;
  background: ${ink};
  color: #ffffff;
  text-align: center;
  font: 700 clamp(10.5px, 2.6vw, 12.5px) / 1.4 ${ui};
  letter-spacing: 0.06em;
  text-transform: uppercase;

  @media ${PHONE} {
    letter-spacing: 0.04em;
  }
`

const Header = styled.header`
  padding: 20px 24px 0;
`

const Logo = styled.a`
  display: inline-flex;
  line-height: 0;

  img {
    height: 46px;
    width: auto;
    display: block;
  }

  @media ${PHONE} {
    img {
      height: 40px;
    }
  }
`

const Hero = styled.section`
  padding: clamp(12px, 1.5vw, 20px) 24px clamp(28px, 3.5vw, 44px);
`

const Banner = styled(GradientPill)`
  ${riseIn(0)}
`

const H1 = styled.h1`
  margin: 0;
  font-weight: 800;
  font-size: clamp(1.9rem, 8vw, 2.5rem);
  line-height: 1.08;
  letter-spacing: -0.042em;
  ${riseIn(0.06)}

  span {
    color: #4496a7;
    ${(props) => props.$accentBlock && 'display: block;'}
  }
`

const Lede = styled.p`
  margin: 0;
  max-width: 52ch;
  font-size: clamp(1.1rem, 1.5vw, 1.25rem);
  font-weight: 500;
  line-height: 1.65;
  ${riseIn(0.1)}
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || 14}px;
  ${(props) => riseIn(props.$delay)}
`

// the prototype's .byline: Martin's face beside a line on who the visitor gets
const Byline = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  max-width: 660px;
  font-size: 13.5px;
  line-height: 1.45;
  text-align: left;
  color: ${muted};
  ${riseIn(0.34)}

  img {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    object-position: 50% 35%;
    border: 2px solid ${iceLine};
  }

  strong {
    color: ${ink};
  }
`

/**
 * The hero: the headline, the promises, the buy button and the price, or the trial's terms on a trial page, and
 * the byline where a page has one.
 */
const HeroSection = ({ offer }) => {
  const { trusted, hero, trial, centred } = useFocus()

  return (
    <>
      <TrustStrip>{trusted}</TrustStrip>
      <Header>
        <Wrap>
          <Link href="/" passHref>
            <Logo aria-label={header.homeLabel}>
              <StaticImage src={header.logo} alt={header.logoAlt} width={220} height={60} />
            </Logo>
          </Link>
        </Wrap>
      </Header>
      <Hero id={IDS.hero}>
        <Wrap>
          <Column $max={780} $gap={22} $centred={centred}>
            <Banner>{hero.banner}</Banner>
            {hero.eyebrow && <Kicker style={{ margin: 0 }}>{hero.eyebrow}</Kicker>}
            <H1 $accentBlock={hero.accentBlock}>
              {hero.headline} <span>{hero.headlineAccent}</span>
            </H1>
            {hero.lede && <Lede>{hero.lede}</Lede>}
            <Group $delay={0.16}>
              <Bullets $ok>
                {hero.checks.map((check) => (
                  <li key={check}>
                    <RichText text={check} />
                  </li>
                ))}
              </Bullets>
            </Group>
            {trial ? (
              <Group $delay={0.24}>
                <TrialCta offer={offer} limited centre={centred} />
              </Group>
            ) : (
              <>
                <Group $gap={18} $delay={0.24}>
                  <BuyButton style={{ alignSelf: 'flex-start' }}>{hero.cta}</BuyButton>
                </Group>
                <Group $delay={0.3}>
                  <PriceBlock offer={offer} />
                </Group>
              </>
            )}
            {hero.byline && (
              <Byline>
                <StaticImage src={hero.byline.photo.src} alt={hero.byline.photo.alt} width={48} height={48} />
                <span>
                  <RichText text={hero.byline.text} />
                </span>
              </Byline>
            )}
          </Column>
        </Wrap>
      </Hero>
    </>
  )
}

export default HeroSection
